import ZButton from "@atoms/ZButton";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { IZFormulaTestProps } from "./interface";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { MergeTagVariable } from "@/Contracts/Documents";
import { testFormula as testFormulaService, FormulaTestResponse } from "../services";
import ZInputFieldForMergeTag from "./ZInputFieldForMergeTag";
import React from "react";
import { Col, Divider, Row } from "antd";
import ZInput from "@molecules/ZInput";
import { EInputWidths } from "@molecules/ZInput/interface";
import ZForm from "../../ZForm";
import { IFormProps, IFormSectionProps } from "../../ZForm/interface";
import { useForm } from "antd/lib/form/Form";

const ZFormulaTest: React.FC<IZFormulaTestProps> = ({
	fieldName,
	editor,
	mergeTags,
	updateTestedFormula,
	updateTestFormulaValidState,
	decimalPlaces
}) => {
	const [form] = useForm();
	const [fieldValueMap, setFieldValueMap] = useState<{
		[key: string]: string;
	}>({});
	const [isLoading, setIsLoading] = useState(false);
	const [sampleOutput, setSampleOutput] = useState<string>("");
	const [inInvalidOutput, setIsInvalidOutput] = useState<boolean>(false);
	const [isOutputFieldHighlighted, setIsOutputFieldHighlighted] =
		useState<boolean>(false);
	const testFormula = useCallback(async () => {
		try {
			await form.validateFields();
			setIsLoading(true);
			let code = editor.getValue();
			uniqueVariables.forEach(variable => {
				const replaceRegex = new RegExp(variable, "g");
				code = code.replace(
					replaceRegex,
					`{{${variableMergeTagMap[variable].variable_name}}}`
				);
			});
			const { result } = await testFormulaService(code, fieldValueMap, decimalPlaces);
			setSampleOutput(`${result}`);
			setIsInvalidOutput(false);
			updateTestFormulaValidState(true);
			updateTestedFormula(code);
			setIsOutputFieldHighlighted(true);
			setTimeout(() => {
				setIsOutputFieldHighlighted(false);
			}, 2000);
		} catch (error) {
			console.error("Error in testing formula", error);
			setSampleOutput("Invalid Output");
			setIsInvalidOutput(true);
			updateTestFormulaValidState(false);
			updateTestedFormula("");
		} finally {
			setIsLoading(false);
		}
	}, [editor, fieldValueMap, decimalPlaces]);

	const [isOnMergeTagsUsedInitialized, setIsOnMergeTagsUsedInitialized] =
		useState<boolean>(false);
	useEffect(() => {
		if (!!mergeTags.length && !isOnMergeTagsUsedInitialized) {
			document.addEventListener("onMergeTagsUsed", () => {
				getAllVariables();
			});
			setIsOnMergeTagsUsedInitialized(true);
		}
		return () => {
			document.removeEventListener("onMergeTagsUsed", () => {
				getAllVariables();
			});
		};
	}, [mergeTags]);
	const [uniqueVariables, setUniqueVariables] = useState<string[]>([]);
	const [variableMergeTagMap, setVariableMergeTagMap] = useState<{
		[key: string]: MergeTagVariable;
	}>({});
	const getAllVariables = useCallback(() => {
		const variablesSet = new Set<string>();
		const editorCode: string = editor.getValue();
		const variables = editorCode.match(/\{\{.*?\}\}/g);
		variables?.forEach(variable => {
			variablesSet.add(variable);
		});
		const uniqueVariables = Array.from(variablesSet);
		setUniqueVariables(Array.from(uniqueVariables));
		uniqueVariables.forEach(variable => {
			if (!variableMergeTagMap[variable]) {
				const mergeTag = mergeTags.find(
					tag => `{{${tag.display_name}}}` === variable
				);
				if (!!mergeTag) {
					setVariableMergeTagMap(prev => ({
						...prev,
						[variable]: mergeTag
					}));
				}
			}
		});
	}, [editor, mergeTags, variableMergeTagMap]);
	const dataSource = useMemo(
		() =>
			uniqueVariables
				.map(variable => variableMergeTagMap[variable])
				.filter(variable => !!variable),
		[uniqueVariables, variableMergeTagMap]
	);
	const sections: Array<IFormSectionProps> = [
		{
			meta: {
				formItemLayout: [10, 14],
				fields: dataSource.map(field => ({
					key: `{{${field.variable_name.replace(/\./g, "_")}}}`,
					label: field.display_name,
					widget: ZInputFieldForMergeTag,
					widgetProps: {
						mergeTag: field
					},
					value: fieldValueMap[`{{${field.variable_name}}}`],
					onChange: (value: string) => {
						setFieldValueMap(prev => ({
							...prev,
							[field.variable_name]: `${value}`
						}));
					},
					required: true,
					message: `Please enter value for ${field.display_name}`,
					className: "mb-2"
				}))
			}
		}
	];
	const formProps: IFormProps = {
		key: "testFormulaForm",
		className: "test-formula-form",
		form,
		sections,
		submitButtonProps: {
			className: "hidden"
		},
		formFooter: <div />
	};
	return (
		<div className="formula-test-container">
			<div className="formula-test-header">
				<ZTypography.ZText
					className="mr-1"
					weight={EFontWeights.SEMIBOLD}
					level={1}
					colorVariant={9}
				>
					Preview and Test Formula
				</ZTypography.ZText>
				<div className="flex-1" />
				<ZButton
					loading={isLoading}
					onClick={testFormula}
					type="primary"
					ghost
				>
					Test Formula
				</ZButton>
			</div>
			<Divider className="my-4" />
			<Row className="formula-test-output">
				<Col span={10}>{!!fieldName ? fieldName : "[Field Name]"}</Col>
				<Col span={14}>
					<ZInput
						className={`formula-test-output-input ${
							isOutputFieldHighlighted ? "highlight" : ""
						}`}
						width={EInputWidths.full}
						value={!inInvalidOutput ? sampleOutput : ""}
						disabled
						readOnly
					/>
				</Col>
			</Row>
			{!!uniqueVariables.length && <ZForm {...formProps} />}
		</div>
	);
};

export default memo(ZFormulaTest);
